import IconDoctor from "@shared/assets/icons/doctor.png";
import IconNurse from "@shared/assets/user-types/nurse.png";
import IconOrganization from "@shared/assets/user-types/organization.png";
import IconPatient from "@shared/assets/user-types/patient.png";

import { IconType } from "react-icons/lib";

import { Gender } from "@shared/constants/gender";
import { useAuthStore } from "@shared/features/auth/stores/auth";
import { getAddressObject } from "@shared/features/form/utils/getAddressObject";
import { formatPhone } from "@shared/utils/formatPhone";
import { formatBirthdate } from "@shared/utils/formatDate";

import { UserType, UserTypeAvatar, UserTypeIcon } from "../constants/user-type";
import type { AccessPolicy } from "../../../../api/src/access-policy/models/access-policy.model";
import type { Patient } from "../../../../api/src/patient/models/patient.model";
import type { Doctor } from "../../../../api/src/doctor/models/doctor.model";
import type { Assistant } from "../../../../api/src/assistant/models/assistant.model";
import type { Midwife } from "../../../../api/src/midwife/entities/midwife.entity";
import type { InvitedUser } from "../../../../api/src/user/models/invited-user.model";
import {
  COUNTRY_TO_CURRENCY,
  DEFAULT_CURRENCY,
} from "../constants/country-to-currency";

export interface UseUserProfile {
  id: string;
  [key: string]: any;
}

export interface UseUser<Profile = UseUserProfile> {
  id: string;
  userType:
    | "patient"
    | "doctor"
    | "assistant"
    | "organization"
    | "midwife"
    | "dispatchManager"
    | "dispatchOperator";
  username: string;
  chatId?: string;
  hasAccessTo?: AccessPolicy[];
  grantedAccessTo?: AccessPolicy[];
  sldId?: string;
  sdbkey?: string;
  ssessid?: string;
  active: boolean;
  userData: Profile;
  patient?: Patient;
  doctor?: Doctor;
  assistant?: Assistant;
  midwife?: Midwife;
  position?: {
    latitude: number;
    longitude: number;
  };
  channels?: {
    id: string;
    type: string;
    title?: string;
    users: UseUser[];
    messages: unknown[];
    creation: string;
  }[];
  common: {
    name: string;
    lastName: string;
    firstName: string;
    age: number;
    gender: (typeof Gender)[number]["label"];
    phoneNumber: string;
    email: string;
    address: ReturnType<typeof getAddressObject>;
    type: string;
    Icon: IconType;
    avatar: string;
    currency: string;
    profileId: string;
  };
  preferences?: {
    id: string;
    code: string;
    valueString: string;
  }[];
}

export const useUser = <T = UseUserProfile>(sourceUser = null): UseUser<T> => {
  let user = sourceUser;

  if (!user) {
    user = useAuthStore((state) => state.user);
  }

  // user = structuredClone(user);

  if (!user) {
    return null;
  }

  // if ("__useUser" in user) {
  //   return user;
  // }

  // if user is a profile, remap the correct properties
  if (user?.[user?.userType]?.user) {
    const _user = user[user.userType].user;

    user = {
      ..._user,
      ...user,
      user: undefined,
    };
  }

  const data = user?.[user?.userType] || {};

  return {
    __useUser: true,
    ...user,
    get userData() {
      return data;
    },
    common: {
      get name() {
        return typeof data.name === "string"
          ? data.name
          : data.name?.[0]?.text ?? data.nameText;
      },
      get lastName() {
        return data.name?.[0]?.family;
      },
      get firstName() {
        return data.name?.[0]?.given;
      },
      get age() {
        return formatBirthdate(data.birthDate, new Date(), {
          format: ["years"],
        });
        // return differenceInCalendarYears(new Date(), parseISO(data.birthDate));
      },
      get gender() {
        return Gender.find(({ value }) => data.gender === value)?.label;
      },
      get phoneNumber() {
        const phoneNumber = data.telecom?.find(
          ({ system }) => system === "PHONE"
        )?.value;

        try {
          return phoneNumber ? formatPhone(phoneNumber) : undefined;
        } catch (error) {
          console.log(error);

          return undefined;
        }
      },
      get email() {
        return data.telecom?.find(({ system }) => system === "EMAIL")?.value;
      },
      get address() {
        return data.address?.[0] ? getAddressObject(data.address[0]) : null;
      },
      get type() {
        return UserType[user.userType];
      },
      get Icon() {
        return UserTypeIcon[user.userType] ?? UserTypeIcon.DEFAULT;
      },
      get avatar() {
        return (
          {
            patient: IconPatient,
            doctor: IconDoctor,
            assistant: IconNurse,
            midwife: IconNurse,
            organization: IconOrganization,
          }[user.userType] ?? IconDoctor
        );
      },
      get Avatar() {
        return UserTypeAvatar[user.userType] ?? UserTypeAvatar.DEFAULT;
      },
      get currency() {
        return COUNTRY_TO_CURRENCY[this.address?.country] ?? DEFAULT_CURRENCY;
      },
      get profileId() {
        return data.id;
      },
    },
  };
};

export const useInvitedUser = (profile: InvitedUser): UseUser<T> => {
  return useUser({
    [profile.userType]: {
      name: [
        {
          given: profile.firstName,
          family: profile.lastName,
          text: `${profile.firstName} ${profile.lastName}`.trim(),
        },
      ],
      telecom: [
        {
          system: "EMAIL",
          value: profile.email,
        },
        {
          system: "PHONE",
          value: profile.phoneNumber,
        },
      ],
    },
    username: profile.email,
    userType: profile.userType,
    __invitedUser: profile.id,
  });
};

const uncapitalize = (str: string) => {
  return str?.length > 1 ? str.charAt(0).toLowerCase() + str.slice(1) : str;
};

export const useUserFromProfile = <T = UseUserProfile>(
  profile: T,
  userType?: string
): UseUser<T> => {
  const type = uncapitalize(
    userType ||
      profile?.user?.userType ||
      profile?.__typename ||
      profile?.resourceType ||
      ""
  );

  if (type.toLowerCase() === "user") {
    return useUser(profile);
  }

  if (type.toLowerCase() === "inviteduser") {
    return useInvitedUser(profile);
  }

  if (
    ![
      "patient",
      "doctor",
      "assistant",
      "midwife",
      "organization",
      "dispatchmanager",
      "dispatchoperator",
    ].includes(type.toLowerCase())
  ) {
    throw new Error(`Invalid profile type: ${type}`);
  }

  return useUser({ userType: type, [type]: profile });
};
